import React, { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  BaseSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalCommand,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";

export interface ImagePayload {
  altText: string;
  height?: number;
  key?: NodeKey;
  maxWidth?: number;
  src: string;
  width?: number;
}

export const INSERT_IMAGE_COMMAND: LexicalCommand<ImagePayload> = createCommand(
  "INSERT_IMAGE_COMMAND"
);
export function $createImageNode({
  altText,
  height,
  maxWidth = 500,
  src,
  width,
  key,
}: ImagePayload): ImageNode {
  return new ImageNode(src, altText, maxWidth, width, height, key);
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode;
}

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src, width, height } = domNode;
    const node = $createImageNode({ altText, height, src, width });
    return { node };
  }
  return null;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    height?: number;
    maxWidth: number;
    src: string;
    width?: number;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<React.JSX.Element> {
  __src: string;
  __altText: string;
  __width: number | undefined;
  __height: number | undefined;
  __maxWidth: number;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, height, width, maxWidth, src } = serializedNode;
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      src,
      width,
    });
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    element.setAttribute("alt", this.__altText);
    if (this.__width !== undefined) {
      element.setAttribute("width", this.__width.toString());
    }
    if (this.__height !== undefined) {
      element.setAttribute("height", this.__height.toString());
    }
    element.style.objectFit = "contain";
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    maxWidth: number,
    width?: number,
    height?: number,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width;
    this.__height = height;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      height: this.__height,
      maxWidth: this.__maxWidth,
      src: this.getSrc(),
      type: "image",
      version: 1,
      width: this.__width,
    };
  }

  setWidthAndHeight(
    width: number | undefined,
    height: number | undefined
  ): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate(): React.JSX.Element {
    return (
      <ImageWithResizer
        src={this.__src}
        alt={this.__altText}
        width={this.__width}
        height={this.__height}
        maxWidth={this.__maxWidth}
        nodeKey={this.getKey()}
      />
    );
  }
}

const RESIZE_HANDLE_SIZE = 8;

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface ImageWithResizerProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  maxWidth: number;
  nodeKey: NodeKey;
}

const handleStyles: Record<ResizeDirection, React.CSSProperties> = {
  n: { top: -RESIZE_HANDLE_SIZE / 2, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' },
  s: { bottom: -RESIZE_HANDLE_SIZE / 2, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' },
  e: { right: -RESIZE_HANDLE_SIZE / 2, top: '50%', cursor: 'ew-resize', transform: 'translateY(-50%)' },
  w: { left: -RESIZE_HANDLE_SIZE / 2, top: '50%', cursor: 'ew-resize', transform: 'translateY(-50%)' },
  ne: { top: -RESIZE_HANDLE_SIZE / 2, right: -RESIZE_HANDLE_SIZE / 2, cursor: 'nesw-resize' },
  nw: { top: -RESIZE_HANDLE_SIZE / 2, left: -RESIZE_HANDLE_SIZE / 2, cursor: 'nwse-resize' },
  se: { bottom: -RESIZE_HANDLE_SIZE / 2, right: -RESIZE_HANDLE_SIZE / 2, cursor: 'nwse-resize' },
  sw: { bottom: -RESIZE_HANDLE_SIZE / 2, left: -RESIZE_HANDLE_SIZE / 2, cursor: 'nesw-resize' },
};

const directions: ResizeDirection[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

function ImageWithResizer({ src, alt, width, height, maxWidth, nodeKey }: ImageWithResizerProps) {
  const [editor] = useLexicalComposerContext();
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = React.useState(false);
  const [resizeDir, setResizeDir] = React.useState<ResizeDirection | null>(null);
  const [start, setStart] = React.useState<{x: number, y: number, width: number, height: number}>({x: 0, y: 0, width: width || 0, height: height || 0});
  const [isSelected, setIsSelected] = React.useState(false);

  // Stable refs for handlers
  const startRef = React.useRef(start);
  const resizeDirRef = React.useRef(resizeDir);
  const isResizingRef = React.useRef(isResizing);

  React.useEffect(() => { startRef.current = start; }, [start]);
  React.useEffect(() => { resizeDirRef.current = resizeDir; }, [resizeDir]);
  React.useEffect(() => { isResizingRef.current = isResizing; }, [isResizing]);

  // Selection effect: update isSelected when selection changes
  React.useEffect(() => {
    return editor.registerUpdateListener(({editorState}) => {
      editorState.read(() => {
        const selection = $getSelection();
        let selected = false;
        if ($isNodeSelection(selection)) {
          const keys = selection.getNodes().map(n => n.getKey());
          selected = keys.includes(nodeKey);
        }
        setIsSelected(selected);
      });
    });
  }, [editor, nodeKey]);

  const onPointerMove = React.useCallback((e: PointerEvent) => {
    if (!isResizingRef.current || !resizeDirRef.current) return;
    let newWidth = startRef.current.width;
    let newHeight = startRef.current.height;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    if (resizeDirRef.current.includes('e')) newWidth = clamp(startRef.current.width + dx, 20, maxWidth);
    if (resizeDirRef.current.includes('w')) newWidth = clamp(startRef.current.width - dx, 20, maxWidth);
    if (resizeDirRef.current.includes('s')) newHeight = clamp(startRef.current.height + dy, 20, 10000);
    if (resizeDirRef.current.includes('n')) newHeight = clamp(startRef.current.height - dy, 20, 10000);

    if (resizeDirRef.current.length === 2) {
      const aspect = startRef.current.width / startRef.current.height;
      if (Math.abs(dx) > Math.abs(dy)) {
        newHeight = newWidth / aspect;
      } else {
        newWidth = newHeight * aspect;
      }
    }

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setWidthAndHeight(Math.round(newWidth), Math.round(newHeight));
      }
    });
  }, [editor, nodeKey, maxWidth]);

  const onPointerUp = React.useCallback(() => {
    setIsResizing(false);
    setResizeDir(null);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  // Clean up listeners on unmount
  React.useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const onPointerDown = (dir: ResizeDirection) => (e: React.PointerEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeDir(dir);
    setStart({
      x: e.clientX,
      y: e.clientY,
      width: width || imgRef.current?.width || 100,
      height: height || imgRef.current?.height || 100,
    });
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          width: width,
          height: height,
          maxWidth: maxWidth,
          objectFit: 'contain',
          userSelect: 'none',
          pointerEvents: isResizing ? 'none' : 'auto',
          outline: isSelected ? '1px solid grey' : undefined,
        }}
        draggable={false}
        onClick={() => {
          editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if ($isImageNode(node)) {
              console.log("Image node clicked:", node);
              const selection = $getSelection();
              if (!$isNodeSelection(selection)) {
                editor.update(() => {
                  // Use Lexical API to select the node by key
                  // @ts-ignore
                  if (typeof editor.selectNodeByKey === "function") {
                    // @ts-ignore
                    editor.selectNodeByKey(nodeKey);
                  }
                });
                setIsSelected(true);
              }
            }
          });
        }}
      />
      {isSelected && directions.map(dir => (
        <span
          key={dir}
          style={{
            position: 'absolute',
            width: RESIZE_HANDLE_SIZE,
            height: RESIZE_HANDLE_SIZE,
            background: '#fff',
            border: '1px solid grey',
            borderRadius: 2,
            boxSizing: 'border-box',
            zIndex: 10,
            ...handleStyles[dir],
          }}
          onPointerDown={onPointerDown(dir)}
        />
      ))}
    </span>
  );
}

export default function ImagePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error("ImagePlugin: ImageNode not registered on editor");
    }

    return editor.registerCommand<ImagePayload>(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        console.log("Inserting ImageNode with payload:", payload);
        const imageNode = $createImageNode(payload);
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const node = selection.getNodes()[0];
          if ($isImageNode(node)) {
            node.replace(imageNode);
          }
        } else {
          selection?.insertNodes([imageNode]);
        }

        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}
